import { authLogger } from './logger';
import type { SubscriptionUpdatedPayload } from '@/types/subscription';

type AuthEventType =
  | 'auth:subscription_updated'
  | 'auth:logout'
  | 'auth:error'
  | 'token:updated'
  | 'websocket:connect'
  | 'websocket:disconnect';

interface AuthEvent<T = unknown> {
  type: AuthEventType;
  payload?: T;
  timestamp: number;
}

interface ErrorPayload {
  error: Error;
  context?: string;
}

/**
 * Event-based communication system for authentication
 * Replaces direct store updates to prevent race conditions
 */
export class AuthEventBus {
  private static listeners = new Map<
    AuthEventType,
    Set<(event: AuthEvent) => void>
  >();

  /**
   * Subscribe to auth events
   */
  static on(
    eventType: AuthEventType,
    callback: (event: AuthEvent) => void
  ): () => void {
    if (!this.listeners.has(eventType)) {
      this.listeners.set(eventType, new Set());
    }

    this.listeners.get(eventType)!.add(callback);

    // Return unsubscribe function
    return () => {
      this.listeners.get(eventType)?.delete(callback);
    };
  }

  /**
   * Emit an auth event
   */
  static emit<T = unknown>(eventType: AuthEventType, payload?: T): void {
    const event: AuthEvent<T> = {
      type: eventType,
      payload,
      timestamp: Date.now(),
    };

    authLogger.debug(`Emitting auth event: ${eventType}`, payload);

    const listeners = this.listeners.get(eventType);
    if (listeners) {
      listeners.forEach((callback) => {
        try {
          callback(event);
        } catch (error) {
          authLogger.error(
            `Error in auth event listener for ${eventType}:`,
            error
          );
        }
      });
    }
  }

  /**
   * Emit subscription updated event
   */
  static emitSubscriptionUpdated(data: SubscriptionUpdatedPayload): void {
    this.emit<SubscriptionUpdatedPayload>('auth:subscription_updated', data);
  }

  /**
   * Emit logout event
   */
  static emitLogout(): void {
    this.emit('auth:logout');
  }

  /**
   * Emit error event
   */
  static emitError(error: Error, context?: string): void {
    this.emit<ErrorPayload>('auth:error', { error, context });
  }

  /**
   * Emit WebSocket connect event
   */
  static emitWebSocketConnect(userId: string, token: string): void {
    this.emit('websocket:connect', { userId, token });
  }

  /**
   * Emit WebSocket disconnect event
   */
  static emitWebSocketDisconnect(): void {
    this.emit('websocket:disconnect');
  }

  /**
   * Clear all listeners (useful for testing)
   */
  static clear(): void {
    this.listeners.clear();
  }
}

// Export convenience functions
export const authEvents = {
  on: AuthEventBus.on.bind(AuthEventBus),
  emit: AuthEventBus.emit.bind(AuthEventBus),
  emitSubscriptionUpdated:
    AuthEventBus.emitSubscriptionUpdated.bind(AuthEventBus),
  emitLogout: AuthEventBus.emitLogout.bind(AuthEventBus),
  emitError: AuthEventBus.emitError.bind(AuthEventBus),
  emitWebSocketConnect: AuthEventBus.emitWebSocketConnect.bind(AuthEventBus),
  emitWebSocketDisconnect:
    AuthEventBus.emitWebSocketDisconnect.bind(AuthEventBus),
};
